import React from 'react';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';

import { setLocalStorageItem, getLocalStorageItem } from 'utils/localStorage';
import {
  ITaxonomyItem,
  SpendTypeEnum,
  IRuleItem,
} from 'components/Classification';
import { ApiContext } from 'context/ApiContext';
import { RuleCellRenderer } from '../RuleCellRenderer';
import { PERMISSIONS } from 'utils/constants';

const WRAPPER_THEME_STYLES = {
  height: '100%',
  width: '100vw',
};

const LOCAL_STORAGE_SUFFIX = '-RULES-COLUMNS';
const RULE_CELL_RENDERER = 'ruleCellRenderer';

const applyCellParamsToColumnDefs = (colDefs: any, cellParams: any): any => {
  return colDefs.map((column: any) => {
    let enhancedColumn = { ...column };

    if (column.children) {
      enhancedColumn = {
        ...enhancedColumn,
        children: applyCellParamsToColumnDefs(column.children, cellParams),
      };
    }

    if (column.cellRenderer === RULE_CELL_RENDERER) {
      enhancedColumn = { ...enhancedColumn, cellRendererParams: cellParams };
    }

    return enhancedColumn;
  });
};

interface IProps {
  updateRule(data: IRuleItem): Promise<any>;
  gridConfig: AgGridReactProps['gridOptions'];
  taxonomyOptions: ITaxonomyItem[];
  spendType: SpendTypeEnum;
  columnGroups: string[];
  permissions: string[];
}

export class ClassificationRulesTable extends React.Component<IProps> {
  gridApi = null as AgGridReactProps['api'];
  gridColumnApi = null as AgGridReactProps['columnApi'];

  getGridOptions = () => {
    return {
      defaultColDef: {
        filter: 'agTextColumnFilter',
        sortable: true,
        resizable: true,
        floatingFilter: true,
      },
      sideBar: {
        toolPanels: [
          {
            id: 'columns',
            labelDefault: 'Columns',
            labelKey: 'columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel',
            toolPanelParams: {
              suppressRowGroups: true,
              suppressValues: true,
              suppressPivotMode: true,
            },
          },
          'filters',
        ],
      },
      frameworkComponents: {
        [RULE_CELL_RENDERER]: RuleCellRenderer,
      },
      getRowNodeId: (data: any) => {
        return data.ruleId;
      },
      onGridReady: this.onGridReady,
      onColumnMoved: this.handleColumnsChange,
      onColumnResized: this.handleColumnsChange,
      onColumnVisible: this.handleColumnsChange,
      onColumnPinned: this.handleColumnsChange,
      onColumnValueChanged: this.handleColumnsChange,
      onSortChanged: this.handleColumnsChange,
      onRowDataChanged: this.handleDataChanged,
      ...this.getGridConfigWithParams(),
    };
  };

  getGridConfigWithParams = () => {
    const {
      gridConfig,
      taxonomyOptions,
      spendType,
      columnGroups,
      permissions,
    } = this.props;

    if (!gridConfig) {
      return gridConfig;
    }
    const hasWritePermission = permissions.includes(
      PERMISSIONS.SETTINGS_CLASSIFICATION_WRITE
    );

    const cellParams = {
      editRule: this.editRule,
      deleteRule: this.deleteRule,
      columnGroups,
      spendType,
      taxonomyOptions,
      hasWritePermission,
    };

    return {
      ...gridConfig,
      columnDefs: applyCellParamsToColumnDefs(
        gridConfig.columnDefs,
        cellParams
      ),
    };
  };

  editRule = (updateData: IRuleItem) => {
    const { updateRule } = this.props;

    return updateRule(updateData).then(() => {
      if (!this.gridApi) {
        return;
      }

      const currentRow = this.gridApi.getRowNode(updateData.ruleId.toString());

      currentRow.setData({
        ...currentRow.data,
        ...updateData,
      });
    });
  };

  deleteRule = (deleteData: IRuleItem) => {
    const { updateRule } = this.props;

    return updateRule(deleteData).then(() => {
      if (!this.gridApi) {
        return;
      }

      const currentRow = this.gridApi.getRowNode(deleteData.ruleId.toString());

      this.gridApi.applyTransaction({ remove: [currentRow.data] });
    });
  };

  handleDataChanged = () => {
    if (this.gridApi) {
      this.gridApi.sizeColumnsToFit();
      this.setStateFromLocalStorage();
    }
  };

  handleColumnsChange = ({ columnApi }: any) => {
    if (!columnApi) {
      return;
    }

    const { spendType } = this.props;
    const columnState = columnApi.getColumnState();

    setLocalStorageItem(spendType + LOCAL_STORAGE_SUFFIX, columnState);
  };

  onGridReady = (params: any) => {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    this.setStateFromLocalStorage();
    params.api.sizeColumnsToFit();
  };

  setStateFromLocalStorage = () => {
    const { spendType, gridConfig } = this.props;
    const colState =
      getLocalStorageItem(spendType + LOCAL_STORAGE_SUFFIX) ||
      gridConfig?.columnDefs;

    if (
      this.gridColumnApi &&
      colState !== null &&
      this.gridColumnApi !== null
    ) {
      this.gridColumnApi.setColumnState(colState);
    }
  };

  render() {
    const gridOptions = this.getGridOptions();

    return (
      <div style={WRAPPER_THEME_STYLES} className="ag-theme-balham">
        <AgGridReact {...gridOptions} />
      </div>
    );
  }
}

export default (props: any) => (
  <ApiContext.Consumer>
    {({ permissions }) => (
      <ClassificationRulesTable {...props} permissions={permissions} />
    )}
  </ApiContext.Consumer>
);
